import { Head, useForm } from "@inertiajs/react";
import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Layout } from "@/components/Layout";
import { WeekView } from "@/components/WeekView";

type Options = {
  clubs: { id: number; name: string; home: boolean }[];
  sports: string[];
  durationsBySport: Record<string, number[]>;
  preferencesBySport: Record<string, string[]>;
  defaultPreferenceBySport: Record<string, string>;
  modes: string[];
  contacts: number;
  earliestDate: string;
  leadDays: number;
  bookingWindowDays: number;
  defaults: { startTime?: string; duration?: number; courtPreference?: string };
};

type Allowance = {
  unlimited: boolean;
  plan: string | null;
  singles: { used: number; limit: number | null; remaining: number | null };
  series: { used: number; limit: number | null; remaining: number | null };
};

export default function New({ options, allowance }: { options: Options; allowance: Allowance }) {
  const { defaults } = options;
  // options.sports arrives in the order people actually use them here, tennis
  // first, so the head of the list is the sensible default.
  const initialSport = options.sports[0] ?? "tennis";

  const { data, setData, post, processing, errors } = useForm({
    // Home club first: it is where a member actually plays.
    club_id: options.clubs.find((c) => c.home)?.id ?? options.clubs[0]?.id ?? 0,
    sport: initialSport,
    date: "",
    start_time: defaults.startTime ?? DEFAULT_START_TIME,
    desired_duration: defaults.duration ?? 60,
    // Defaults to the full length — book exactly what was asked, and snipe for
    // it — unless the member opts to accept a shorter session.
    min_duration: defaults.duration ?? 60,
    court_preference: options.defaultPreferenceBySport[initialSport] ?? "any",
    backup_times: [] as string[],
    repeat_weekly: false,
    allow_court_switch: false,
  });

  // Durations vary by sport — padel is 60min only here — so switching sport
  // must not leave a length selected that the club does not sell.
  const durations = options.durationsBySport[data.sport] ?? [60];

  function chooseSport(sport: string) {
    const available = options.durationsBySport[sport] ?? [60];
    const keep = available.includes(data.desired_duration);
    setData((current) => ({
      ...current,
      sport,
      desired_duration: keep ? current.desired_duration : (available[0] ?? 60),
      min_duration: keep ? current.min_duration : (available[0] ?? 60),
      court_preference: options.defaultPreferenceBySport[sport] ?? "any",
    }));
  }

  // Changing the length resets the minimum to it (exact), and never lets the
  // minimum sit above the length.
  function chooseDuration(minutes: number) {
    setData((current) => ({ ...current, desired_duration: minutes, min_duration: minutes }));
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    post("/booking_requests");
  }

  // Two members can hold consecutive slots, which is the only way to get two
  // hours at peak. Saying so up front explains why the option is limited.
  const canTile = options.contacts > 1;
  const [showWeek, setShowWeek] = useState(false);

  // Up to three fallback start times, tried in order after the main one. Times
  // already chosen (main or another alternative) are not offered again.
  const MAX_ALTERNATIVES = 3;
  const takenTimes = new Set([data.start_time, ...data.backup_times]);

  // Default a new alternative to the half-hour just after the latest time
  // already chosen, so adding them builds a natural ladder (18:30 → 19:00 →
  // 19:30) rather than jumping to 06:00. Steps earlier if nothing later is
  // free, and falls back to any free slot at the edges.
  function nextDefaultTime() {
    const anchor = [data.start_time, ...data.backup_times]
      .reduce((latest, t) => (t > latest ? t : latest), data.start_time);
    const at = START_TIMES.indexOf(anchor);

    for (let i = at + 1; i < START_TIMES.length; i++) {
      if (!takenTimes.has(START_TIMES[i]!)) return START_TIMES[i]!;
    }
    for (let i = at - 1; i >= 0; i--) {
      if (!takenTimes.has(START_TIMES[i]!)) return START_TIMES[i]!;
    }
    return START_TIMES.find((t) => !takenTimes.has(t));
  }

  function addAlternative() {
    const next = nextDefaultTime();
    if (next && data.backup_times.length < MAX_ALTERNATIVES) {
      setData("backup_times", [...data.backup_times, next]);
    }
  }
  function changeAlternative(index: number, value: string) {
    setData("backup_times", data.backup_times.map((t, i) => (i === index ? value : t)));
  }
  function removeAlternative(index: number) {
    setData("backup_times", data.backup_times.filter((_, i) => i !== index));
  }

  // Said before the form is filled in, not after it is refused. The limit is
  // on what is held AT ONCE: a court booked for Saturday occupies a slot until
  // Saturday has passed.
  const noSinglesLeft = !allowance.unlimited && (allowance.singles.remaining ?? 1) === 0;
  const noSeriesLeft = !allowance.unlimited && (allowance.series.remaining ?? 1) === 0;

  // Every error that no field renders itself, so nothing can be rejected
  // without the user being told why.
  const FIELD_ERRORS = ["sport", "date", "start_time"];
  const otherErrors = Object.entries(errors)
    .filter(([field]) => !FIELD_ERRORS.includes(field))
    .map(([, message]) => message)
    .filter(Boolean) as string[];

  return (
    <Layout title="New request">
      <Head title="New request" />

      <form onSubmit={submit} className="max-w-lg space-y-5">
        {!allowance.unlimited && (
          <div
            className={`rounded-lg border px-3 py-2 text-sm ${
              noSinglesLeft
                ? "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200"
                : "border-neutral-200 bg-neutral-50 text-neutral-600 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-300"
            }`}
          >
            {noSinglesLeft ? (
              <>
                <strong>You have used all {allowance.singles.limit} of your bookings.</strong>{" "}
                Cancel one, or wait until a court you have booked has been played.
              </>
            ) : (
              <>
                {allowance.singles.remaining} of {allowance.singles.limit} bookings left
                {allowance.series.limit !== null && (
                  <> · {allowance.series.remaining} of {allowance.series.limit} weekly</>
                )}
                . A booked court counts until it has been played.
              </>
            )}
          </div>
        )}
        {/* Anything not attached to a named field used to be dropped silently:
            a rejected request simply reloaded the form with no explanation,
            which reads as the button not working. */}
        {otherErrors.length > 0 && (
          <div
            role="alert"
            className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200"
          >
            {otherErrors.map((message) => <p key={message}>{message}</p>)}
          </div>
        )}

        {/* Only worth asking when there is a choice to make. */}
        {options.clubs.length > 1 && (
          <label className="block">
            <span className="text-sm font-medium">Club</span>
            <select
              value={data.club_id}
              onChange={(e) => setData("club_id", Number(e.target.value))}
              className={inputClass}
            >
              {options.clubs.map((club) => (
                <option key={club.id} value={club.id}>
                  {club.name}{club.home ? " (your club)" : ""}
                </option>
              ))}
            </select>
            <span className="mt-1 block text-xs text-neutral-500">
              Courts at another club may open on a different day to your own.
            </span>
          </label>
        )}

        <Choice
          label="Sport" value={data.sport} options={options.sports}
          format={capitalise}
          onChange={chooseSport} error={errors.sport}
        />

        <label className="block">
          <span className="text-sm font-medium">Date</span>
          <input
            type="date" value={data.date} required
            min={options.earliestDate}
            onChange={(e) => setData("date", e.target.value)}
            className={inputClass}
          />
          <span className="mt-1 block text-xs text-neutral-500">
            At least {options.leadDays} days ahead. David Lloyd removes bookings
            closer than that unless enough players are named on them.
          </span>
          {errors.date && <Error>{errors.date}</Error>}
        </label>

        <Choice
          label="Start time" value={data.start_time} options={START_TIMES}
          onChange={(v) => setData("start_time", v)} error={errors.start_time}
        />

        <Choice
          label="Length" value={String(data.desired_duration)}
          options={durations.map(String)}
          format={(v) => `${v} minutes`}
          onChange={(v) => chooseDuration(Number(v))}
        />
        {data.desired_duration > 90 && !canTile && (
          <Note>
            Two hours needs a second linked member at peak times, and you have one
            contact. Off-peak it can still be booked as a single slot.
          </Note>
        )}

        {/* How short a session the member will settle for. Defaults to the full
            length, so by default the bot books exactly that and keeps sniping
            for it. Choosing less lets it take a shorter session at release and
            then keep trying to extend it to the full length. */}
        {durations.filter((d) => d < data.desired_duration).length > 0 && (
          <div>
            <Choice
              label="Shortest you'll accept"
              value={String(data.min_duration)}
              options={durations.filter((d) => d <= data.desired_duration).map(String)}
              format={(v) => (Number(v) === data.desired_duration ? `${v} minutes (exact)` : `${v} minutes`)}
              onChange={(v) => setData("min_duration", Number(v))}
            />
            {data.min_duration < data.desired_duration && (
              <span className="mt-1 block text-xs text-neutral-500">
                If the full {data.desired_duration} minutes isn't free at release, it will book
                at least {data.min_duration} and keep trying to extend it to {data.desired_duration}.
              </span>
            )}
          </div>
        )}

        {/* Alternative start times. At a busy hour the requested slot may never
            come free; a couple of fallbacks, tried in order at the SAME length,
            turn a likely miss into a likely booking. */}
        <div className="rounded-lg border border-neutral-200 px-3 py-3 dark:border-neutral-800">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Alternative times</span>
            <span className="text-xs text-neutral-500">optional · up to {MAX_ALTERNATIVES}</span>
          </div>
          <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
            Tried in order if {data.start_time} is taken, each for the same {data.desired_duration} minutes.
          </p>

          {data.backup_times.map((time, index) => (
            <div key={index} className="mt-2 flex items-end gap-2">
              <div className="flex-1">
                <Choice
                  label={`Alternative ${index + 1}`}
                  value={time}
                  options={START_TIMES.filter((t) => t === time || !takenTimes.has(t))}
                  onChange={(v) => changeAlternative(index, v)}
                />
              </div>
              <button
                type="button"
                onClick={() => removeAlternative(index)}
                className="mb-1 rounded-md px-2 py-2 text-sm text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                aria-label={`Remove alternative ${index + 1}`}
              >
                Remove
              </button>
            </div>
          ))}

          {data.date && data.backup_times.length > 0 && (
            <div className="mt-2 space-y-1">
              {data.backup_times.map((time, index) => (
                <Chances
                  key={`chance-${index}`}
                  clubId={data.club_id}
                  sport={data.sport}
                  date={data.date}
                  startTime={time}
                  duration={data.desired_duration}
                  compact
                />
              ))}
            </div>
          )}

          {data.backup_times.length < MAX_ALTERNATIVES && (
            <button
              type="button"
              onClick={addAlternative}
              className="mt-2 text-sm font-medium text-brand-600 hover:underline"
            >
              + Add an alternative time
            </button>
          )}
          {errors.backup_times && <Error>{errors.backup_times}</Error>}
        </div>

        {/* Not a choice: a date whose courts are already open cannot be booked
            "at release", and one that has not opened yet cannot be sniped.
            Asking would only invite picking wrong. */}
        <WhenToTry date={data.date} windowDays={options.bookingWindowDays} />

        {/* "When should I play?" comes before "will this slot work?". The
            weekly view answers it — how each day of the week looks at this
            time — without committing to a date. */}
        <button
          type="button"
          onClick={() => setShowWeek(true)}
          className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm font-medium hover:border-brand-500 dark:border-neutral-700"
        >
          See the week at {data.start_time}
        </button>

        {showWeek && (
          <WeekView
            clubId={data.club_id}
            sport={data.sport}
            startTime={data.start_time}
            duration={data.desired_duration}
            onClose={() => setShowWeek(false)}
          />
        )}

        {/* The odds were only ever shown after the request existed, which is
            after the member committed to a time. Knowing that this slot is
            almost never free is worth far more while it can still be changed. */}
        <Chances
          clubId={data.club_id}
          sport={data.sport}
          date={data.date}
          startTime={data.start_time}
          duration={data.desired_duration}
        />

        {/* A repeat is a property of the slot just chosen, so the weekday comes
            from the date rather than being asked for a second time. */}
        <label className={`flex items-start gap-3 rounded-lg border border-neutral-200 px-3 py-3 dark:border-neutral-800 ${noSeriesLeft ? "opacity-60" : ""}`}>
          <input
            type="checkbox"
            checked={data.repeat_weekly && !noSeriesLeft}
            disabled={noSeriesLeft}
            onChange={(e) => setData("repeat_weekly", e.target.checked)}
            className="mt-0.5 h-4 w-4"
          />
          <span className="text-sm">
            <span className="font-medium">
              Repeat every week
              {noSeriesLeft && <span className="ml-2 font-normal text-amber-700 dark:text-amber-500">maximum reached</span>}
            </span>
            <span className="mt-0.5 block text-neutral-500">
              {noSeriesLeft
                ? `Your plan allows ${allowance.series.limit} weekly ${allowance.series.limit === 1 ? "game" : "games"}. Stop one first to repeat a different slot.`
                : data.date
                  ? `Court Pilot will ask for ${weekdayOf(data.date)} ${data.start_time} every week, booking each one the moment its courts open. Pause or stop it any time.`
                  : "Pick a date first — the day of the week comes from it."}
            </span>
          </span>
        </label>

        <Choice
          label="Court preference"
          value={data.court_preference}
          options={options.preferencesBySport[data.sport] ?? [ "any" ]}
          format={preferenceLabel}
          onChange={(v) => setData("court_preference", v)}
        />

        {/* Only matters for a session longer than one slot — a single hour is
            one court regardless. */}
        {data.desired_duration > 60 && (
          <label className="flex items-start gap-3 rounded-lg border border-neutral-200 px-3 py-3 dark:border-neutral-800">
            <input
              type="checkbox"
              checked={data.allow_court_switch}
              onChange={(e) => setData("allow_court_switch", e.target.checked)}
              className="mt-0.5 h-4 w-4"
            />
            <span className="text-sm">
              <span className="font-medium">Take the best court each hour</span>
              <span className="mt-0.5 block text-neutral-500">
                By default a multi-hour session stays on one court. Tick this to grab the
                best available court each hour instead — you may change courts between hours.
              </span>
            </span>
          </label>
        )}

        <button
          type="submit" disabled={processing || noSinglesLeft}
          className="w-full rounded-lg bg-brand-600 px-4 py-2.5 font-medium text-white transition hover:bg-brand-700 disabled:opacity-50"
        >
          {noSinglesLeft ? "No bookings left" : processing ? "Saving…" : "Create request"}
        </button>
      </form>
    </Layout>
  );
}

// Half-hour slots only — David Lloyd sells nothing else, so offering minutes
// invites a time that can never be booked.
//
// Plain chronological order. These once ran evenings-first so the list opened
// at 6pm, which is when most people book — but that put 21:30 above 06:00 and
// scrolling up from a mid-morning slot showed later times, not earlier ones.
// The evening default belongs on the selected value, not the ordering.
const START_TIMES = halfHours(6, 22);

// When nothing is remembered. Evening is when most people are trying to play.
const DEFAULT_START_TIME = "18:00";

function weekdayOf(iso: string) {
  return new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", { weekday: "long" });
}

function halfHours(from: number, to: number) {
  const times: string[] = [];
  for (let hour = from; hour < to; hour += 1) {
    times.push(`${String(hour).padStart(2, "0")}:00`);
    times.push(`${String(hour).padStart(2, "0")}:30`);
  }
  return times;
}

const inputClass =
  "mt-1 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 dark:border-neutral-700 dark:bg-neutral-900";

function Choice({
  label, value, options, onChange, format, error,
}: {
  label: string; value: string; options: string[];
  onChange: (v: string) => void; format?: (v: string) => string; error?: string;
}) {
  return (
    <label className="block">
      <span className="text-sm font-medium">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className={inputClass}>
        {options.map((option) => (
          <option key={option} value={option}>{format ? format(option) : option}</option>
        ))}
      </select>
      {error && <Error>{error}</Error>}
    </label>
  );
}

function WhenToTry({ date, windowDays }: { date: string; windowDays: number }) {
  if (!date) {
    return (
      <Info title="When we will try">
        Pick a date and Court Pilot will work out whether to wait for release or
        start watching straight away.
      </Info>
    );
  }

  const days = Math.round(
    (new Date(`${date}T00:00:00`).getTime() - new Date().setHours(0, 0, 0, 0)) / 86_400_000,
  );

  return days > windowDays ? (
    <Info title="Booking at release">
      These courts open {days - windowDays} day{days - windowDays === 1 ? "" : "s"} from
      now, {windowDays} days before the session. Court Pilot will try the moment
      they do.
    </Info>
  ) : (
    <Info title="Watching for cancellations">
      These courts are already open, so the rush has passed. Court Pilot will
      keep checking until the session in case somebody gives one back.
    </Info>
  );
}

type Forecast = {
  available: boolean;
  enough?: boolean;
  observations?: number;
  sampleWeeks?: number;
  chance?: number | null;
  // Other members in front of you, not counting yourself.
  ahead?: number;
  // You already have a court request for this day, so David Lloyd would not
  // let you book a second one.
  alreadyYours?: boolean;
  typicalFree?: number | null;
  byKind?: Record<string, number | null>;
  // Carpet and bubble at tennis, indoor and outdoor at padel.
  kinds?: string[];
};

// History for the same weekday and time, fetched as the slot is chosen.
function Chances({
  clubId, sport, date, startTime, duration, compact = false,
}: {
  clubId: number; sport: string; date: string; startTime: string; duration: number; compact?: boolean;
}) {
  const [forecast, setForecast] = useState<Forecast | null>(null);

  useEffect(() => {
    if (!date) {
      setForecast(null);
      return;
    }

    // The slot changes with every click, so a stale in-flight answer must not
    // overwrite a newer one.
    const controller = new AbortController();
    const query = new URLSearchParams({
      club_id: String(clubId), sport, date, start_time: startTime, duration: String(duration),
    });

    fetch(`/forecast?${query}`, { signal: controller.signal, headers: { Accept: "application/json" } })
      .then((response) => (response.ok ? response.json() : null))
      .then(setForecast)
      .catch(() => {});

    return () => controller.abort();
  }, [clubId, sport, date, startTime, duration]);

  if (compact) {
    if (!forecast?.available) {
      return <div className="text-xs text-neutral-400">{startTime} · no history yet</div>;
    }
    if (forecast.alreadyYours) {
      return <div className="text-xs text-neutral-400">{startTime} · you already have this day</div>;
    }
    if (!forecast.enough) {
      return <div className="text-xs text-neutral-400">{startTime} · too little history</div>;
    }
    const pct = Math.round((forecast.chance ?? 0) * 100);
    return (
      <div className="text-xs text-neutral-500 dark:text-neutral-400">
        {startTime} · {pct}% had a court in {forecast.observations} past weeks
      </div>
    );
  }

  if (!forecast?.available) return null;

  // Said first, because it decides whether the rest is worth reading: David
  // Lloyd allows one booking a day, so this date is spoken for.
  if (forecast.alreadyYours) {
    return (
      <Info title="You already have this day">
        David Lloyd allows one booking a member a day, so you cannot take a second
        court on this date. Pick another day, or change the request you already have.
      </Info>
    );
  }

  if (!forecast.enough) {
    return (
      <Info title="Not enough history yet">
        We have watched this slot {forecast.observations ?? 0}{" "}
        {forecast.observations === 1 ? "time" : "times"} so far. Court Pilot records
        what was bookable every morning at release, so this fills in over time.
      </Info>
    );
  }

  const kinds = forecast.kinds ?? [];
  // One kind means there is nothing to distinguish — "3 courts", not
  // "3 court". More than one is worth naming: carpet and bubble play
  // differently, and in summer the dome is the reason to say no.
  const split = kinds.length === 1
    ? `${forecast.byKind?.[kinds[0]!] ?? 0} court${(forecast.byKind?.[kinds[0]!] ?? 0) === 1 ? "" : "s"}`
    : kinds.map((kind) => `${forecast.byKind?.[kind] ?? 0} ${kind}`).join(" and ");
  const ahead = forecast.ahead ?? 0;
  const percent = Math.round((forecast.chance ?? 0) * 100);

  // Courts bookable leads, because the chance is near 100% almost everywhere —
  // the readings are taken at release, when hardly anything has gone — so it
  // cannot tell a comfortable slot from a tight one. Split by surface, and no
  // lumped total: it hid that most of the courts were under the dome.
  return (
    <Info title={`Usually ${split || "no courts"} bookable`}>
      At this time across {forecast.observations} past weeks.{" "}
      {ahead === 0
        ? `You would be first in the queue, and ${percent}% of those weeks had a court to give you.`
        : `${ahead} other ${ahead === 1 ? "member is" : "members are"} ahead of you for this slot,
           so you would need ${ahead + 1} courts free — which happened in ${percent}% of those weeks.`}
    </Info>
  );
}

function Info({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3 text-sm dark:border-neutral-800 dark:bg-neutral-900">
      <span className="font-medium">{title}</span>
      <span className="mt-0.5 block text-neutral-500">{children}</span>
    </div>
  );
}

function Note({ children }: { children: React.ReactNode }) {
  return (
    <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-950 dark:text-amber-200">
      {children}
    </p>
  );
}

const PREFERENCE_LABELS: Record<string, string> = {
  carpet_first: "Carpet, then bubble",
  carpet_only: "Carpet only",
  bubble_first: "Bubble, then carpet",
  bubble_only: "Bubble only",
  indoor_first: "Indoor, then outdoor",
  indoor_only: "Indoor only",
  outdoor_first: "Outdoor, then indoor",
  outdoor_only: "Outdoor only",
  any: "Any court",
};

function preferenceLabel(value: string) {
  return PREFERENCE_LABELS[value] ?? value.replace(/_/g, " ");
}

function capitalise(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function Error({ children }: { children: string }) {
  return <span className="mt-1 block text-sm text-red-600">{children}</span>;
}
